// Shared look for every react-select dropdown (searchable and multi select).
// It mirrors the native .form-select in styles/forms.css exactly: same height,
// text size, padding, arrow image (--select-arrow) and placeholder colour, so
// all three select types look the same side by side. Values are the design tokens from
// styles/tokens.css, so a theme change applies here too.

/**
 * @param {object} [options]
 * @param {boolean} [options.invalid] red border, as for a field with an error
 * @param {object} [overrides] extra react-select style functions, merged on top
 */
export const getSelectStyles = ({ invalid = false } = {}, overrides = {}) => {
  const base = {
    control: (provided, state) => ({
      ...provided,
      minHeight: "var(--control-height)",
      borderRadius: "var(--radius-sm)",
      backgroundColor: state.isDisabled
        ? "var(--color-surface-sunken)"
        : invalid
          ? "var(--color-danger-soft)"
          : "var(--color-surface)",
      borderColor: invalid
        ? "var(--color-danger)"
        : state.isFocused
          ? "var(--color-primary)"
          : "var(--color-border)",
      boxShadow: state.isFocused
        ? invalid
          ? "var(--focus-ring-danger)"
          : "var(--focus-ring-soft)"
        : "none",
      fontSize: "var(--font-size-base)",
      lineHeight: 1.5,
      transition: "border-color var(--transition-fast), box-shadow var(--transition-fast)",
      "&:hover": {
        borderColor: invalid
          ? "var(--color-danger)"
          : state.isFocused
            ? "var(--color-primary)"
            : "var(--color-border-strong)",
      },
    }),
    // Text starts at the same inset as a native select
    valueContainer: (provided) => ({
      ...provided,
      padding: "0.125rem 0.25rem 0.125rem var(--select-padding-x)",
    }),
    placeholder: (provided) => ({ ...provided, color: "var(--color-text-muted)", marginLeft: 0, marginRight: 0 }),
    singleValue: (provided, state) => ({
      ...provided,
      color: state.isDisabled ? "var(--color-text-subtle)" : "var(--color-text)",
      marginLeft: 0,
      marginRight: 0,
    }),
    input: (provided) => ({ ...provided, color: "var(--color-text)", margin: 0, paddingTop: 0, paddingBottom: 0 }),
    // Explicit surface colour: react-select's default menu is white, which
    // made the translucent hover tint unreadable in dark mode
    menu: (provided) => ({
      ...provided,
      backgroundColor: "var(--color-surface-raised)",
      borderRadius: "var(--radius-sm)",
      boxShadow: "var(--shadow-popover)",
      border: "1px solid var(--color-popover-border)",
      overflow: "hidden",
      zIndex: 20,
    }),
    menuPortal: (provided) => ({ ...provided, zIndex: 9999 }),
    menuList: (provided) => ({ ...provided, backgroundColor: "var(--color-surface-raised)" }),
    noOptionsMessage: (provided) => ({ ...provided, color: "var(--color-text-muted)" }),
    loadingMessage: (provided) => ({ ...provided, color: "var(--color-text-muted)" }),
    option: (provided, state) => ({
      ...provided,
      fontSize: "var(--font-size-base)",
      padding: "0.5rem var(--select-padding-x)",
      cursor: "pointer",
      backgroundColor: state.isSelected
        ? "var(--color-primary)"
        : state.isFocused
          ? "var(--color-option-hover)"
          : "var(--color-surface-raised)",
      color: state.isSelected ? "var(--color-on-primary)" : "var(--color-text)",
      ":active": { backgroundColor: "var(--color-primary-border)" },
    }),
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: "var(--color-primary-soft)",
      borderRadius: "var(--radius-sm)",
    }),
    multiValueLabel: (provided) => ({ ...provided, color: "var(--color-primary-text)" }),
    multiValueRemove: (provided) => ({
      ...provided,
      color: "var(--color-primary-text)",
      ":hover": { backgroundColor: "var(--color-primary-border)", color: "var(--color-primary-text)" },
    }),
    // Native selects have no divider before the arrow
    indicatorSeparator: () => ({ display: "none" }),
    // Same arrow image, size and spacing as a native .form-select
    dropdownIndicator: (provided) => ({
      ...provided,
      width: "var(--select-arrow-space)",
      padding: 0,
      alignSelf: "stretch",
      backgroundImage: "var(--select-arrow)",
      backgroundRepeat: "no-repeat",
      backgroundPosition: "right var(--select-padding-x) center",
      backgroundSize: "var(--select-arrow-size)",
      "& svg": { display: "none" },
    }),
    clearIndicator: (provided) => ({
      ...provided,
      padding: "0 0.25rem",
      color: "var(--color-text-muted)",
      ":hover": { color: "var(--color-danger-text)" },
    }),
  };

  // Overrides run after the base style for the same part
  const merged = { ...base };
  Object.entries(overrides).forEach(([part, fn]) => {
    const baseFn = base[part];
    merged[part] = baseFn ? (provided, state) => fn(baseFn(provided, state), state) : fn;
  });
  return merged;
};

export default getSelectStyles;
